/**
 * AdoraCare - C2 Workboard view model (C2-WORKBOARD-UI-BUILD-01).
 *
 * Pure, React-free helpers + types + the role-only access decision for the
 * front-half operator Workboard. Kept side-effect-free (no React, no CSS, no
 * api import at runtime) so the vitest suite can exercise every counter /
 * filter / status-derivation by direct unit call and render the presentational
 * component via react-dom/server (this repo has NO jsdom; see
 * WorkboardPage.test.tsx).
 *
 * HARD DATA RULE (per the C2 package): every counter / filter / chip / column
 * is built ONLY from the fields GET /api/me/work-actions actually returns -
 * FollowupRow: { id, leadId, patientName, reason, dueContext (always
 * "Assigned"), channel (always "Call"), status, createdAt, leadStage }.
 * STITCH-2 SCOPE 5 added `leadStage` (the lead's validated canon stage) to
 * the read, so the STAGE-AWARE action strip and the row stage chip below are
 * contract-backed; the response still has NO due_at / clinic / assigned-role
 * / priority field, so no such affordance exists here.
 *
 * Status values come ONLY from real API responses: 'open' from the read, and
 * 'done' / 'dismissed' from the transition (POST /api/work-actions/:id/transition)
 * or the work-item auto-close on a successful stage advance. So the
 * open/completed counters and the status filter are honest.
 */

import type { DashboardMe } from '@/dashboard/types';
import type { FollowupRow } from '@/walkin/WalkinFollowupsPanel';
import type { LeadStageTarget } from '@/dashboard/api';

export type { FollowupRow };

// --- Role-only access decision (mirrors decideRoutingSettingsAccess) --------

/**
 * The two roles that operate the front-half Workboard. A role-only gate,
 * keyed off the server-authenticated me.role - NO /me flag, NO feature flag,
 * NO new nav section (mirrors the routing-settings guard). Internal surfaces
 * ship default-on per ADR-0016; backend route auth (the /me/work-actions and
 * /leads/:id/stage scope checks) remains the real security boundary.
 */
export const WORKBOARD_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['receptionist', 'coc']);

export type WorkboardAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Pure access decision for the Workboard route guard. Mirrors
 * decideNavSectionAccess / decideRoutingSettingsAccess: wait while loading;
 * redirect to /login on error or no scope; redirect to the caller's own
 * homePath when the role is outside the Workboard set; otherwise render.
 */
export function decideWorkboardAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): WorkboardAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!WORKBOARD_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// --- Front-half stage targets (ADR-0017 Decision 4) ------------------------

/**
 * The FRONT-HALF stage targets the Workboard exposes as per-row advance
 * buttons. The backend (api/routes/lead-stage.ts) REJECTS `contacted`
 * (disposition-derived) and the DEFERRED `patient` / `converted_to_revenue`
 * positive-conversion gate (Phase E) - this UI NEVER offers those two.
 */
export interface StageTargetOption {
  readonly to: LeadStageTarget;
  readonly label: string;
}

export const FRONT_HALF_STAGE_TARGETS: readonly StageTargetOption[] = [
  { to: 'qualified', label: 'Qualify' },
  { to: 'appointment_booked', label: 'Booked' },
  { to: 'arrived', label: 'Arrived' },
  { to: 'lost', label: 'Lost' },
];

/** Human label for a stage token (for success feedback after an advance). */
export function stageTargetLabel(to: string): string {
  const found = FRONT_HALF_STAGE_TARGETS.find((t) => t.to === to);
  return found ? found.label : to;
}

// --- STAGE-AWARE actions (STITCH-2 SCOPE 5 / F16; founder ruling 2026-07-07) -

/** One stage action offered on a row / drawer strip. */
export interface StageAwareAction {
  readonly to: LeadStageTarget;
  readonly label: string;
  readonly primary: boolean;
}

/** Plain display label for a row's current stage chip (or null = no chip). */
export const ROW_STAGE_LABEL: Readonly<Record<string, string>> = {
  new: 'New lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  appointment_booked: 'Booked',
  arrived: 'Arrived',
  patient: 'Patient',
  // V-1 (founder-review corrective PR #271): the EXPLICIT CRM revenue-stage
  // copy (matching the rail / timeline / lead-events label homes) so the bare
  // "Converted" can never be mistaken for the payment-derived commercial state.
  converted_to_revenue: 'Converted to revenue',
  lost: 'Lost',
};

/**
 * The stage-aware action strip, derived from the lead's CURRENT stage (the
 * founder matrix): New lead -> primary Qualify, secondary Lost; Qualified ->
 * primary Booked, secondary Lost; Booked -> primary Arrived, secondary Lost.
 * `contacted` (derived) still qualifies. NO Reschedule, NO No-show (V4-P1);
 * the post-arrival stages and terminals offer nothing here. A null/unknown
 * stage offers NO stage action (honest: never a button that will 400) --
 * Done/Dismiss remain the task controls either way. A just-qualified lead is
 * therefore never re-offered Qualify: its stage is `qualified`, whose strip
 * starts at Booked.
 */
export function stageAwareActions(
  leadStage: string | null | undefined,
): readonly StageAwareAction[] {
  switch (leadStage) {
    case 'new':
    case 'contacted':
      return [
        { to: 'qualified', label: 'Qualify', primary: true },
        { to: 'lost', label: 'Lost', primary: false },
      ];
    case 'qualified':
      return [
        { to: 'appointment_booked', label: 'Booked', primary: true },
        { to: 'lost', label: 'Lost', primary: false },
      ];
    case 'appointment_booked':
      return [
        { to: 'arrived', label: 'Arrived', primary: true },
        { to: 'lost', label: 'Lost', primary: false },
      ];
    default:
      return [];
  }
}

// --- Status helpers + counters ---------------------------------------------

/** The work-action transition vocabulary (matches work-actions.ts exactly). */
export type WorkActionTransition = 'done' | 'dismissed';

/** A row is actionable only while its status is the read's 'open'. */
export function isOpenStatus(status: string): boolean {
  return status === 'open';
}

export interface WorkboardCounts {
  readonly total: number;
  readonly open: number;
  readonly completed: number;
}

/**
 * Open / completed / total counts over the loaded rows. `completed` = any row
 * no longer 'open' (done or dismissed via a real transition / auto-close); it
 * is NOT a fabricated figure - every status came from an API response.
 */
export function summarizeWorkActions(
  rows: readonly FollowupRow[],
): WorkboardCounts {
  let open = 0;
  for (const r of rows) if (isOpenStatus(r.status)) open += 1;
  return { total: rows.length, open, completed: rows.length - open };
}

// --- Filtering --------------------------------------------------------------

export type WorkboardStatusFilter = 'open' | 'completed' | 'all';

export interface WorkboardFilters {
  /** open (default) shows the actionable queue; completed shows actioned rows. */
  readonly status: WorkboardStatusFilter;
  /** A `reason` (action-type) label, or null for all action types. */
  readonly reason: string | null;
  /** Free-text match over patient name + lead id. */
  readonly search: string;
}

/** Default view prioritizes the actionable open queue (per the C2 package). */
export const DEFAULT_WORKBOARD_FILTERS: WorkboardFilters = {
  status: 'open',
  reason: null,
  search: '',
};

/** Distinct action-type labels present in the rows, alphabetically sorted. */
export function distinctReasons(rows: readonly FollowupRow[]): string[] {
  const seen = new Set<string>();
  for (const r of rows) if (r.reason) seen.add(r.reason);
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

// --- Row guidance: WHY this task exists + a next-action hint (F3) ------------

/** One plain-language WHY line + a one-line next-action hint for a work row. */
export interface WorkboardGuidance {
  readonly why: string;
  readonly nextAction: string;
}

/**
 * Maps the EXISTING work-action reason label (api/routes/work-actions.ts
 * REASON_LABELS) to a plain-language WHY the task exists + a one-line next-action
 * hint -- the same plain-language bar as the Train D leakage queue. It NEVER
 * invents patient specifics: an unknown or missing reason renders an honest
 * generic line (RECEPTIONIST-EXPERIENCE-STITCH-V1, F3).
 */
const WORKBOARD_GUIDANCE: Record<string, WorkboardGuidance> = {
  'New lead follow-up': {
    why: 'New lead - no contact logged yet.',
    nextAction: 'Call the lead and log the outcome.',
  },
  'Missed call callback': {
    why: 'They called and we missed it.',
    nextAction: 'Call back and log the outcome.',
  },
  'Walk-in patient link review': {
    why: 'A walk-in needs linking to a patient record.',
    nextAction: 'Open the lead and confirm the patient match.',
  },
  'Lead not contacted escalation': {
    why: 'Still no contact - this has been waiting.',
    nextAction: 'Call now and log the outcome.',
  },
  'Appointment confirmation follow-up': {
    why: 'An appointment needs confirming.',
    nextAction: 'Confirm the appointment with the patient.',
  },
};

export function workboardGuidance(reason: string | null): WorkboardGuidance {
  const known = reason === null ? undefined : WORKBOARD_GUIDANCE[reason];
  return (
    known ?? {
      why: 'This task is on your queue.',
      nextAction: 'Open the lead and take the next step.',
    }
  );
}

/** Canonical Lead-360 drill href for a work row (numeric leadId = safeLeadId). */
export function workboardLeadHref(leadId: number): string {
  return `/dashboard/leads/${leadId}`;
}

/** Count rows in a given status bucket (for the status filter pill counts). */
export function countByStatusFilter(
  rows: readonly FollowupRow[],
  status: WorkboardStatusFilter,
): number {
  if (status === 'all') return rows.length;
  if (status === 'open') {
    return rows.reduce((n, r) => (isOpenStatus(r.status) ? n + 1 : n), 0);
  }
  return rows.reduce((n, r) => (isOpenStatus(r.status) ? n : n + 1), 0);
}

/** Count rows matching a reason within the active status bucket (pill counts). */
export function countByReason(
  rows: readonly FollowupRow[],
  status: WorkboardStatusFilter,
  reason: string | null,
): number {
  return filterWorkActions(rows, { status, reason, search: '' }).length;
}

/**
 * Apply the active filters. Pure - returns a new array. Search matches the
 * patient name and the lead id (with or without a leading '#').
 */
export function filterWorkActions(
  rows: readonly FollowupRow[],
  filters: WorkboardFilters,
): FollowupRow[] {
  const q = filters.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (filters.status === 'open' && !isOpenStatus(r.status)) return false;
    if (filters.status === 'completed' && isOpenStatus(r.status)) return false;
    if (filters.reason !== null && r.reason !== filters.reason) return false;
    if (q.length > 0) {
      const hay = `${r.patientName} #${r.leadId} ${r.leadId}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * Replace ONE row's status in place (used after a successful transition or
 * stage advance). The row STAYS in the list - marked completed/dismissed and
 * muted - so the open queue shrinks while the completed counter grows, instead
 * of a row silently vanishing. Pure: returns a new array.
 */
export function applyStatusToRow(
  rows: readonly FollowupRow[],
  id: number,
  status: string,
): FollowupRow[] {
  return rows.map((r) => (r.id === id ? { ...r, status } : r));
}

/**
 * Reflect a successful STAGE ADVANCE in the loaded rows (STITCH-2 SCOPE 5):
 * the backend closes EVERY open work item for the lead
 * (closeOpenWorkItemsForLead, api/routes/lead-stage.ts), so every open row of
 * that lead flips to 'done' here (completed increments -- the founder ruling
 * "stage action marks the task Done, never silent removal"), and every row of
 * the lead carries the new stage so the strip re-derives (a just-qualified
 * lead never re-offers Qualify). Pure: returns a new array.
 */
export function applyStageAdvanceToRows(
  rows: readonly FollowupRow[],
  leadId: number,
  toStage: string,
): FollowupRow[] {
  return rows.map((r) =>
    r.leadId === leadId
      ? {
          ...r,
          status: isOpenStatus(r.status) ? 'done' : r.status,
          leadStage: toStage,
        }
      : r,
  );
}

// --- Presentation maps ------------------------------------------------------

export interface StatusBadge {
  readonly className: string;
  readonly label: string;
}

/**
 * Status -> badge className + label, using ONLY existing token-backed badge
 * classes (no ad-hoc colors). Open is the clear/actionable state; done and
 * dismissed are de-emphasized at the row level (workboard__row--done).
 */
export function statusBadge(status: string): StatusBadge {
  switch (status) {
    case 'open':
      return { className: 'badge badge--info', label: 'Open' };
    case 'done':
      return { className: 'badge badge--success', label: 'Done' };
    case 'dismissed':
      return { className: 'badge badge--info', label: 'Dismissed' };
    default:
      return { className: 'badge badge--info', label: status || 'Unknown' };
  }
}

// --- Relative age (pure; inject `nowMs` so it is deterministic in tests) ----

/**
 * Compact relative age from an ISO `createdAt`. Returns '-' when null/unparsable.
 * `nowMs` is injected (the component passes Date.now()) so the function stays
 * pure and unit-testable without a real clock.
 */
export function formatRelativeAge(
  createdAt: string | null,
  nowMs: number,
): string {
  if (createdAt === null || createdAt === undefined || createdAt === '') {
    return '-';
  }
  const then = Date.parse(createdAt);
  if (Number.isNaN(then)) return '-';
  const diffMs = Math.max(0, nowMs - then);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// --- Error message helper ---------------------------------------------------

/** Pull an HTTP status off an unknown error without importing PortalApiError. */
function errorStatus(error: unknown): number | null {
  if (error !== null && typeof error === 'object' && 'status' in error) {
    const s = (error as { status?: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return null;
}

/**
 * Human-readable message for a failed transition / stage advance. A 409 (the
 * backend's atomic open-guard / stage compare-and-set losing the race) gets the
 * fixed, non-alarming refresh prompt; any other error surfaces the backend's
 * own PHI-free message, with a safe generic fallback.
 */
export function workboardActionErrorMessage(error: unknown): string {
  if (errorStatus(error) === 409) {
    return 'This lead was already updated by someone else. Refresh and try again.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

/** True when the error is a concurrency conflict (offer a Refresh affordance). */
export function isConflictError(error: unknown): boolean {
  return errorStatus(error) === 409;
}
